<template>
  <div
    v-if="stepData"
    class="step"
  >
    <component
      :is="question.questionComponent"
      v-for="question in questions"
      :key="question.questionId || question.questionComponent"
      product-name="van"
      step-id="vehicle"
      :question-id="question.questionId"
      v-bind="question.params || {}"
      :form-observer="formObserver"
      :form-data="form"
      :step-data="stepData"
      :step-shared-data="stepSharedData"
      @toast="showToast($event)"
      @reset-observer="$emit('reset-observer')"
      @input="stepData = { ...stepData, ...$event }"
      @set-step-shared-data="setStepSharedData"
    />
  </div>
</template>
<script>
import { mapActions } from 'vuex';
import { cloneDeep } from 'lodash';
import useExperimentMinimiseHelptext from '@/composables/useExperimentMinimiseHelptext';

export default {
  name: 'form-van-step-1',
  inject: [
    'constants',
    'config',
  ],
  props: {
    formObserver: {
      type: Object,
      required: true,
    },
    form: {
      type: Object,
      required: true,
    },
  },
  data () {
    return {
      stepData: null,
      stepSharedData: {},
    };
  },
  watch: {
    stepData: {
      handler (val) {
        this.$emit('step-data', val);
      },
      immediate: true,
    },
  },
  computed: {
    questions () {
      return [
        {
          questionComponent: 'question-vehicle',
          questionId: 'vehicle',
        },
        {
          questionComponent: 'question-about',
          questionId: 'about',
          params: {
            renderAfter: ['vehicleConfirm'],
            questions: [
              {
                questionId: 'vehicle-side',
                questionComponent: 'question-vehicle-side',
                params: {
                  customFields: {
                    field: 'vehicleSide',
                  },
                  title: 'Is your vehicle right or left hand drive?',
                },
              },
              {
                questionId: 'vehicle-alarm',
                questionComponent: 'question-vehicle-alarm',
                params: {
                  subtitle: 'Is the vehicle fitted with an alarm/immobiliser?',
                  helpText: 'You can find this information in your vehicle handbook. If you have a Thatcham approved security device fitted, many insurance providers may require a certificate of installation.',
                  customFields: {
                    field: 'vehicleAlarm',
                  },
                },
              },
              {
                questionId: 'vehicle-tracking-device',
                questionComponent: 'question-base-radio-boolean',
                params: {
                  customFields: {
                    field: 'vehicleTrackingDevice',
                  },
                  subtitle: 'Does the vehicle have a tracking device?',
                  helpText: 'A tracker or similar is a device which will help recover your vehicle if it\'s stolen. You can find this information in the your Vehicle Hand book.',
                  validationRules: {
                    field: {
                      required: false,
                    },
                  },
                  defaultValues: {
                    field: false,
                  },
                },
              },
              {
                questionId: 'vehicle-import',
                questionComponent: 'question-base-radio-boolean',
                params: {
                  customFields: {
                    field: 'vehicleGreyOrImport',
                  },
                  title: 'Is the vehicle imported?',
                  helpText: 'Any vehicle that was not made specifically to UK specification for sale in this country is considered an import. You can find this information in the Vehicle Registration Document (V5C Logbook).',
                },
              },
            ],
          },
        },
        {
          questionComponent: 'question-base-dropdown',
          questionId: 'seats-of-vehicle',
          params: {
            renderAfter: ['vehicleConfirm'],
            customFields: {
              field: 'seatsOfVehicle',
            },
            title: 'How many seats does the vehicle have?',
            helpText: 'If you are unsure, count how many seatbelts the van has.',
            optionsConfig: {
              options: [
                { value: 1, text: '1' },
                { value: 2, text: '2' },
                { value: 3, text: '3' },
                { value: 4, text: '4' },
                { value: 5, text: '5' },
                { value: 6, text: '6' },
                { value: 7, text: '7' },
                { value: 8, text: '8' },
                { value: 9, text: '9' },
              ],
            },
            validationRules: {
              field: {
                required: true,
              },
            },
            validationErrors: {
              field: {
                required: 'Please choose a number.',
              },
            },
          },
        },
        {
          questionComponent: 'question-estimated-value',
          questionId: 'estimated-value',
          params: {
            renderAfter: ['vehicleConfirm'],
            customFields: {
              vehicleLookupPrice: 'vehicleLookupPrice',
              estimatedValue: 'estimatedValue',
            },
            validationRules: {
              estimatedValue: {
                required: true,
                integer: true,
                between: [0, 99999999],
                minValue: 50,
              },
            },
            validationErrors: {
              estimatedValue: {
                required: 'Please enter the estimated value of your vehicle.',
                integer: 'Please only enter numbers e.g. £5000.',
                between: 'The estimated value of your vehicle must be between £50 and £99999999.',
                minValue: 'The estimated value of your vehicle must be greater than £50.',
              },
            },
            title: 'Roughly, how much is the vehicle worth?',
            helpText: 'Please enter how much your vehicle would be worth in pounds if you sold it today.',
            inputPrefix: '£',
            placeholder: 'e.g. 5000',
            maxLength: 8,
          },
        },
        {
          questionComponent: 'question-modifications',
          questionId: 'modifications',
          params: {
            renderAfter: ['vehicleConfirm'],
            helpText: {
              value: `
                <p>
                  Modifications are non-standard changes made to the vehicle after manufacture, including new spoilers, alloy wheels etc.
                </p>
                <p>
                  You can add as many modifications as you like. Some insurers may offer a discount for particular modifications. You don't need to include any Optional Extras that were added by the manufacturer when the vehicle was built.
                </p>
                <p>
                  If you have not declared a modification and you make a claim, then your claim may be refused and your insurance will be void.
                </p>
              `,
              collapsible: true,
              openerText: this.isExperimentMinimiseHelptext ? 'Understanding Vehicle Modifications' : 'Tell me more about the vehicle modifications',
            },
          },
        },
        {
          questionComponent: 'question-base-dropdown',
          questionId: 'body-type',
          params: {
            renderAfter: ['vehicleConfirm'],
            customFields: {
              field: 'bodyType',
            },
            title: 'How would you describe the van\'s body type?',
            helpText: 'If you can\'t find your vehicle\'s body type, please select ‘Other’.',
            optionsConfig: {
              options: {
                V: 'Van',
                P: 'Pickup',
                LV: 'Light Van',
                L: 'Luton',
                BV: 'Box Van',
                T: 'Tipper',
                C: 'Curtainside',
                D: 'Dropside',
                F: 'Flatbed',
                M: 'Motorhome',
                IV: 'Ice Cream Van',
                MV: 'Mobile Catering Van',
                VR: 'Van - Refridgerated',
                O: 'Other',
              },
            },
            validationRules: {
              field: {
                required: true,
              },
            },
            validationErrors: {
              field: {
                required: 'Please choose your van\'s body type.',
              },
            },
          },
        },
        {
          questionComponent: 'question-vehicle-purchase',
          questionId: 'vehicle-purchase',
          params: {
            renderAfter: ['vehicleConfirm'],
          },
        },
        {
          questionComponent: 'question-owned-registered',
          questionId: 'owner-registered',
          params: {
            renderAfter: ['vehicleConfirm'],
          },
        },
        {
          questionComponent: 'question-base-radio-list',
          questionId: 'vehicle-kept',
          params: {
            renderAfter: ['vehicleConfirm'],
            customFields: {
              field: 'vehicleKept',
            },
            title: 'Where is the vehicle normally kept overnight?',
            helpText: 'Please select where it is kept <strong><u>overnight</u></strong> the majority of the time.',
            optionsConfig: {
              otherPlaceholder: 'Please select the location',
              mainItems: ['R', 'D', 'G'],
              options: {
                R: 'Driveway',
                D: 'On the Road at Home',
                G: 'In a Locked Garage',
                P: 'In a Residential Car Park',
                S: 'In a Secure Car Park',
                C: 'In a Carport',
                F: 'In a Forces Base Car Park',
                UP: 'In an Unsecure Car Park',
                U: 'In an Unlocked Garage',
                W: 'In a Work Car Park',
                H: 'On the Road Away From Home',
                RP: 'In a Railway Car Park',
              },
            },
            validationRules: {
              field: {
                required: true,
              },
            },
            validationErrors: {
              field: {
                required: 'Please choose an answer.',
              },
            },
          },
        },
        {
          questionComponent: 'question-base-radio-list',
          questionId: 'type-of-use',
          params: {
            renderAfter: ['vehicleConfirm'],
            customFields: {
              field: 'typeOfUse',
            },
            title: 'What will you use the vehicle for?',
            helpText: {
              value: `
                <p>
                  <b>Social, Domestic, Pleasure and Commuting</b> - day to day driving, including to and from a permanent place of work. This may not include any journeys that you might make while at work, such as driving to a client's office.
                </p>
                <p>
                  <b>Social, Domestic and Pleasure only</b> - Day to day driving, excluding to and from a place of work.
                </p>
                <p>
                  <b>Business Use for Carriage of Own Goods</b> - carrying items or equipment that are owned by either you or by other people, that you need to carry out your work. Typically includes day to day driving, including to a permanent place of work.
                </p>
                <p>
                  <b>Business Use for carriage of goods for hire or reward (Haulage)</b> - carrying items that you are transporting, and receiving payment for doing so. Examples may include catalogue deliveries, goods delivery or fast food delivery. Typically includes day to day driving, including to a permanent place of work.
                </p>
                <p>
                  Your employer will usually ask you to have Business Use if it is needed for your job.
                </p>
              `,
              collapsible: true,
              openerText: 'Tell me more about vehicle use types',
            },
            optionsConfig: {
              options: {
                1: 'Social, Domestic, Pleasure and Commuting',
                2: 'Social, Domestic and Pleasure Only',
                3: 'Business Use for Carriage of Own Goods',
                5: 'Business Use for Carriage of Goods for Hire or Reward (Haulage)',
              },
            },
            validationRules: {
              field: {
                required: true,
              },
            },
            validationErrors: {
              field: {
                required: 'Please choose a type.',
              },
            },
          },
        },
        {
          questionComponent: 'question-base-number',
          questionId: 'annual-mileage',
          params: {
            renderAfter: ['vehicleConfirm'],
            customFields: {
              field: 'annualMileage',
            },
            title: 'What is your annual mileage?',
            subtitle: 'Including any business mileage',
            helpText: {
              value: 'It\'s easiest to think about how many miles you drive per week and multiply this up over the year (which should also include longer journeys for weekend trips etc). If other drivers will have access to the vehicle, then include any miles that they might make.',
              collapsible: this.isExperimentMinimiseHelptext,
              openerText: 'Estimate annual mileage',
            },
            inputSuffix: 'miles',
            placeholder: 'e.g. 5000',
            maxLength: 6,
            validationRules: {
              field: {
                required: true,
                between: [1, 999999],
              },
            },
            validationErrors: {
              field: {
                required: 'Please enter your mileage e.g. 5000.',
                between: 'Please enter a numeric value between 1 and 999999.',
              },
            },
          },
        },
        {
          // TODO Need to investigate dynamic validation rules
          questionComponent: 'question-business-mileage',
          questionId: 'business-mileage',
          params: {
            renderAfter: ['vehicleConfirm'],
            showRules: {
              typeOfUse: {
                isAny: {
                  other: [3, 5],
                },
                required: true,
              },
            },
            defaultValues: {
              businessMileage: null,
            },
          },
        },
        {
          questionComponent: 'question-base-radio-boolean',
          questionId: 'vehicle-racking',
          params: {
            renderAfter: ['vehicleConfirm'],
            customFields: {
              field: 'vehicleRacking',
            },
            title: 'Is your vehicle fitted with any internal or external racking?',
            helpText: this.vehicleRackingHelpText(),
            showRules: {
              typeOfUse: {
                isAny: {
                  other: [3, 5],
                },
                required: true,
              },
            },
          },
        },
        {
          questionComponent: 'question-base-radio-boolean',
          questionId: 'carrying-hazardous-goods',
          params: {
            renderAfter: ['vehicleConfirm'],
            customFields: {
              field: 'carryingHazardousGoods',
            },
            title: 'Will you be carrying hazardous goods?',
            helpText: 'If you carry goods that are toxic, explosive, corrosive or of a flammable nature, insurance providers will need to know.',
            defaultValues: {
              field: false,
            },
            showRules: {
              typeOfUse: {
                isAny: {
                  other: [3, 5],
                },
                required: true,
              },
            },
          },
        },
        {
          questionComponent: 'question-base-radio-boolean',
          questionId: 'hazardous-sites',
          params: {
            renderAfter: ['vehicleConfirm'],
            customFields: {
              field: 'hazardousSites',
            },
            title: 'Will you be visiting hazardous sites?',
            helpText: 'If you visit sites where chemicals, explosives or other dangerous substances are used or stored, the insurance providers will need to know.',
            defaultValues: {
              field: false,
            },
            showRules: {
              typeOfUse: {
                isAny: {
                  other: [3, 5],
                },
                required: true,
              },
            },
          },
        },
        {
          questionComponent: 'question-base-radio-boolean',
          questionId: 'bad-driver',
          params: {
            renderAfter: ['vehicleConfirm'],
            customFields: {
              field: 'badDriver',
            },
            title: 'Is there a bad driver hotline displayed on the vehicle?',
            helpText: this.badDriverHelpText(),
            showRules: {
              typeOfUse: {
                isAny: {
                  other: [3, 5],
                },
                required: true,
              },
            },
          },
        },
        {
          questionComponent: 'question-vehicle-signage',
          questionId: 'vehicle-signage',
          params: {
            renderAfter: ['vehicleConfirm'],
            showRules: {
              typeOfUse: {
                isAny: {
                  other: [3, 5],
                },
                required: true,
              },
            },
            defaultValues: {
              vehicleSignage: null,
            },
          },
        },
        {
          questionComponent: 'question-base-radio-list',
          questionId: 'type-of-company',
          params: {
            renderAfter: ['vehicleConfirm'],
            customFields: {
              field: 'typeOfCompany',
            },
            title: 'Type of Company?',
            helpText: '<p>A sole trader is a single person owning or running a business.</p><p>A limited company is a business with shareholders.</p>',
            optionsConfig: {
              options: {
                S: 'Sole Trader',
                L: 'Limited Company',
              },
            },
            showRules: {
              typeOfUse: {
                isAny: {
                  other: [3, 5],
                },
                required: true,
              },
            },
            defaultValues: {
              field: null,
            },
            validationRules: {
              field: {
                required: true,
              },
            },
            validationErrors: {
              field: {
                required: 'Please select company type.',
              },
            },
          },
        },
        {
          questionComponent: 'question-base-number',
          questionId: 'business-established',
          params: {
            renderAfter: ['vehicleConfirm'],
            customFields: {
              field: 'businessEstablished',
            },
            showRules: {
              typeOfUse: {
                isAny: {
                  other: [3, 5],
                },
                required: true,
              },
            },
            validationRules: {
              field: {
                required: true,
                integer: true,
                maxYear: 'currentYear',
                minYear: 'currentYear - 200',
              },
            },
            validationErrors: {
              field: {
                required: 'Please enter the year the business was established.',
                integer: 'Please only enter numbers e.g. 2010.',
                maxYear: 'Your business cannot have been established in the future.',
                minYear: 'The business must have been established within the last 200 years.',
              },
            },
            title: 'What year was the business established? (If not known enter current year)',
            helpText: {
              value: 'Insurance providers may provide a discount for established businesses.',
              priceSensitive: true,
            },
            placeholder: 'e.g. 2010',
            maxLength: 4,
            withoutSeparators: true,
          },
        },
      ];
    },
  },
  methods: {
    ...mapActions(['showToast']),
    setStepSharedData (data) {
      this.stepSharedData = { ...this.stepSharedData, ...data };
    },
    // [SU-5906] - hide price sensitive blocks for some questions. Using methods because computed does not work inside data()
    isApricot () {
      return this.constants.PARTNERS.Apricot.includes(this.config.partner);
    },
    vehicleRackingHelpText () {
      return !this.isApricot() && {
        value: 'Some insurance providers may provide a discount if the vehicle has any racking.',
        priceSensitive: true,
      };
    },
    badDriverHelpText () {
      return !this.isApricot() && {
        value: 'Some insurance providers may provide a discount if this is the case.',
        priceSensitive: true,
      };
    },
    // [SU-5906] - end
  },
  async created () {
    this.$emit('tracking', 'vehicle');
    this.stepData = cloneDeep(this.form.vehicle);
    this.formObserver.reset();
  },
  setup () {
    // COMPOSABLES
    const { isExperimentMinimiseHelptext } = useExperimentMinimiseHelptext();

    return {
      isExperimentMinimiseHelptext,
    };
  },
};
</script>
